import { useState, useEffect } from 'react';
import { api } from './api';

const TrashIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke="#e57373" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14H6L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4h6v2" />
  </svg>
);

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function statusFor(inv) {
  const pct = Number(inv.pct_used) || 0;
  if (pct >= 100) return { label: 'Bill client', cls: 'pill-red',   fill: 'full' };
  if (pct >= 80)  return { label: 'Almost full', cls: 'pill-amber', fill: 'warn' };
  if (pct > 0)    return { label: 'In use',      cls: 'pill-blue',  fill: '' };
  return           { label: 'Fresh',       cls: 'pill-green', fill: '' };
}

export default function Invoices({ projects }) {
  const [invoices,  setInvoices]  = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [showForm,  setShowForm]  = useState(false);

  // Form state
  const [projectId,      setProjectId]      = useState('');
  const [invoiceNumber,  setInvoiceNumber]  = useState('');
  const [hoursPurchased, setHoursPurchased] = useState('15');
  const [issuedDate,     setIssuedDate]     = useState(todayStr());
  const [notes,          setNotes]          = useState('');
  const [filterProject,  setFilterProject]  = useState('');
  const [filterOpen,     setFilterOpen]     = useState(false);

  async function load() {
    setLoading(true); setError('');
    try {
      const data = await api.getInvoices(filterProject || undefined, filterOpen);
      setInvoices(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterProject, filterOpen]);

  // Default the form's project when projects load
  useEffect(() => {
    if (projects.length && !projectId) {
      const nonPlatform = projects.find(p => !p.is_platform_work) || projects[0];
      setProjectId(String(nonPlatform.id));
    }
  }, [projects]); // eslint-disable-line

  async function handleCreate(e) {
    e.preventDefault();
    if (!projectId)     return setError('Please select a project');
    if (!invoiceNumber) return setError('Invoice number is required');
    setError('');
    try {
      await api.createInvoice({
        project_id:      parseInt(projectId),
        invoice_number:  invoiceNumber.trim(),
        hours_purchased: parseFloat(hoursPurchased) || 15,
        issued_date:     issuedDate,
        notes:           notes || null,
      });
      setInvoiceNumber(''); setHoursPurchased('15'); setNotes('');
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleDelete(inv) {
    if (!window.confirm(`Delete invoice ${inv.invoice_number}? Time entries attached to it will be unassigned but not deleted.`)) return;
    try {
      await api.deleteInvoice(inv.id);
      load();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <div>
      {/* ── Top controls ── */}
      <div className="report-controls">
        <div className="field">
          <label>Project</label>
          <select value={filterProject} onChange={e => setFilterProject(e.target.value)}>
            <option value="">All projects</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Show</label>
          <select value={filterOpen ? 'open' : 'all'} onChange={e => setFilterOpen(e.target.value === 'open')}>
            <option value="all">All invoices</option>
            <option value="open">Only with hours remaining</option>
          </select>
        </div>
        <div className="field"><label>&nbsp;</label><div /></div>
        <div className="field">
          <label>&nbsp;</label>
          <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
            {showForm ? '× Cancel' : '+ New Invoice'}
          </button>
        </div>
      </div>

      {error && <p className="error-msg">{error}</p>}

      {/* ── New invoice form ── */}
      {showForm && (
        <div className="card" style={{ marginBottom: '1.25rem' }}>
          <div className="form-title">New invoice</div>
          <form onSubmit={handleCreate}>
            <div className="invoice-form-row">
              <div className="field">
                <label>Project</label>
                <select value={projectId} onChange={e => setProjectId(e.target.value)}>
                  {projects.filter(p => !p.is_platform_work).map(p =>
                    <option key={p.id} value={p.id}>{p.name}</option>
                  )}
                </select>
              </div>
              <div className="field">
                <label>Invoice # (from QuickBooks)</label>
                <input value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} placeholder="INV-2026-001" />
              </div>
              <div className="field">
                <label>Hours</label>
                <input type="number" min="0.5" step="0.5" value={hoursPurchased} onChange={e => setHoursPurchased(e.target.value)} />
              </div>
              <div className="field">
                <label>Issued</label>
                <input type="date" value={issuedDate} onChange={e => setIssuedDate(e.target.value)} />
              </div>
              <div className="field">
                <label>&nbsp;</label>
                <button className="btn-primary" type="submit">Create</button>
              </div>
            </div>
            <div className="field" style={{ marginTop: 10 }}>
              <label>Notes (optional)</label>
              <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Quarterly retainer block" />
            </div>
          </form>
        </div>
      )}

      {loading && <p style={{ color: '#737370', padding: '1rem 0' }}>Loading…</p>}

      {/* ── Invoices table ── */}
      {!loading && invoices.length === 0 && (
        <p style={{ color: '#737370', padding: '1.5rem 0', textAlign: 'center' }}>
          No invoices yet. Click <strong>+ New Invoice</strong> to create your first 15-hour block.
        </p>
      )}

      {!loading && invoices.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Project</th>
                <th style={{ width: 150 }}>Invoice #</th>
                <th style={{ width: 80,  textAlign: 'right' }}>Purchased</th>
                <th style={{ width: 70,  textAlign: 'right' }}>Used</th>
                <th style={{ width: 180 }}>Remaining</th>
                <th style={{ width: 110 }}>Status</th>
                <th style={{ width: 110 }}>Issued</th>
                <th style={{ width: 36 }}></th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => {
                const status = statusFor(inv);
                const pct = Math.min(100, Number(inv.pct_used) || 0);
                return (
                  <tr key={inv.id}>
                    <td>{inv.project_name}</td>
                    <td style={{ fontFamily: 'SFMono-Regular, monospace', fontSize: 13 }}>
                      {inv.invoice_number}
                    </td>
                    <td style={{ textAlign: 'right' }}>{Number(inv.hours_purchased).toFixed(1)}</td>
                    <td style={{ textAlign: 'right' }}>{Number(inv.hours_used).toFixed(1)}</td>
                    <td>
                      <div className="progress-wrap">
                        <div className="progress-bar">
                          <div className={`progress-fill ${status.fill}`} style={{ width: `${pct}%` }} />
                        </div>
                        <span style={{ fontSize: 12, color: '#1a1a1a', whiteSpace: 'nowrap' }}>
                          {Number(inv.hours_remaining).toFixed(1)} hrs
                        </span>
                      </div>
                    </td>
                    <td><span className={`pill ${status.cls}`}>{status.label}</span></td>
                    <td style={{ fontSize: 13, color: '#737370' }}>{inv.issued_date}</td>
                    <td>
                      <button className="trash-btn" onClick={() => handleDelete(inv)} title="Delete invoice">
                        <TrashIcon />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="hint">
        Each invoice represents a pre-paid block (default 15 hrs). When <em>Remaining</em> hits 0, bill the client for another block.
      </p>
    </div>
  );
}
